// 24×24 non-interactive number badge for milestone rows + timeline nodes.
// Uses a single neutral slate tone for the number circle — the status pill
// palette is already colourful enough without the badge competing. We
// deliberately do NOT extend the shared `.badge` component class because
// `.badge` carries `px-2.5 py-0.5` for pill-style tags and that padding
// interferes with vertical centring of a single glyph inside a fixed
// h-6 w-6 box. flex + items-center + justify-center + leading-none
// produces a reliably-centred digit at 24×24.
//
// Slippage states (`overdue`, `late`) override the base slate tone with
// a solid fill — the user found a ring too subtle on the timeline:
//   - overdue (not yet delivered, plannedDate past): solid red
//   - late (delivered after planned, NOT yet accepted): solid amber
//
// `lateHistory` is a separate orthogonal flag for milestones that
// were delivered late but have since been accepted (or paid). The
// base slate tone wins; an amber RING overlays it to surface the
// historical slippage without dominating the number. Mutually exclusive
// with `slippage` — callers compute either-or, not both.
//
// Span, not button — clicking the row chrome / timeline node is the
// interaction. The badge is a status surface only.

import { clsx } from "clsx";
import type { MilestoneDerivedStatus } from "../../../../utils/deriveMilestoneStatus";

export interface MilestoneNumberBadgeProps {
  milestoneNumber: number;
  /** Optional single-letter insertion suffix; renders as "13a". */
  milestoneSuffix?: string;
  urgencyStatus: MilestoneDerivedStatus;
  /** Solid-fill slippage. `overdue` = not delivered yet, plannedDate
   *  past; `late` = delivered after plannedDate AND not yet accepted.
   *  When set, overrides the urgency tone with a solid colour. */
  slippage?: "overdue" | "late" | null;
  /** Late-history ring overlay. Set when a milestone was delivered
   *  after its plannedDate AND has since been accepted (or paid). The
   *  urgency tone (emerald) wins; this paints an amber ring on top to
   *  surface the historical slippage without overriding the
   *  acceptance/paid status. Mutually exclusive with `slippage` —
   *  callers compute either-or, not both. */
  lateHistory?: boolean;
}

const NEUTRAL_BADGE_TONE =
  "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";

const SLIPPAGE_TONE: Record<"overdue" | "late", string> = {
  overdue: "bg-red-500 text-white dark:bg-red-500 dark:text-white",
  late: "bg-amber-500 text-white dark:bg-amber-500 dark:text-white",
};

const SLIPPAGE_LABEL: Record<"overdue" | "late", string> = {
  overdue: "overdue",
  late: "delivered late",
};

export default function MilestoneNumberBadge({
  milestoneNumber,
  milestoneSuffix,
  urgencyStatus,
  slippage = null,
  lateHistory = false,
}: MilestoneNumberBadgeProps) {
  const label = `${milestoneNumber}${milestoneSuffix ?? ""}`;
  const ariaLabelSuffix = slippage
    ? `, ${SLIPPAGE_LABEL[slippage]}`
    : lateHistory
      ? ", was delivered late"
      : "";
  const ariaLabel = `Milestone ${label}, ${urgencyStatus}${ariaLabelSuffix}`;
  return (
    <span
      aria-label={ariaLabel}
      data-slippage={slippage ?? undefined}
      data-late-history={lateHistory ? "true" : undefined}
      className={clsx(
        "inline-flex h-6 w-6 items-center justify-center rounded-full text-caption font-semibold leading-none",
        slippage ? SLIPPAGE_TONE[slippage] : NEUTRAL_BADGE_TONE,
        lateHistory &&
          !slippage &&
          "ring-2 ring-amber-500 ring-offset-1 dark:ring-amber-400 dark:ring-offset-slate-900",
      )}
    >
      {label}
    </span>
  );
}
